import { useState } from "react";
import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/BrandLogo";
import { Download, FileText, ArrowRight, ChevronLeft, ChevronRight, ArrowLeft, X } from "lucide-react";

const PAGES = Array.from({ length: 9 }, (_, i) => `/plan-pages/page-${i + 1}.jpg`);
const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/marketing/plan.pdf`;

const PAGE_TITLES = [
  "Cover",
  "How it Works",
  "Bot-01 Nova · Starter",
  "Bot-02 Pulse · Growth",
  "Bot-03 Quantum · Pro",
  "Bot-04 Apex · Elite",
  "Team Commissions (5 Levels)",
  "Company Shareholder Program",
  "Ranks & Getting Started",
];

export default function Plan() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="min-h-screen bg-[#050A14] text-slate-200">
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/"><BrandLockup /></Link>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="plan-nav-home" className="text-slate-400 hover:text-white text-sm hidden sm:inline-flex items-center gap-1">
            <ArrowLeft size={14}/> Home
          </Link>
          <a
            href={PDF_URL}
            download="next-billion-plan.pdf"
            data-testid="plan-download-pdf"
            className="nb-btn-primary text-sm inline-flex items-center gap-2"
          >
            <Download size={14}/> <span className="hidden sm:inline">Download PDF</span><span className="sm:hidden">PDF</span>
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-14 relative">
        <div className="absolute -top-20 right-0 h-64 w-64 rounded-full bg-[#8AE234]/10 blur-3xl pointer-events-none"/>
        <div className="absolute top-40 -left-20 h-64 w-64 rounded-full bg-nb-cyan/10 blur-3xl pointer-events-none"/>
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-nb-cyan flex items-center gap-2">
            <FileText size={14}/> The Plan
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl text-white mt-3 leading-tight">
            Everything about <span className="text-[#8AE234]">Next Billion</span>,<br/>
            in one place.
          </h1>
          <p className="text-slate-400 mt-5 max-w-2xl text-lg">
            Bots. Team commissions. Shareholder cascade. Ranks. Getting started. Every detail of the plan — laid out on nine premium pages you can flip through here or download as PDF.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={PDF_URL}
              download="next-billion-plan.pdf"
              data-testid="plan-hero-download"
              className="nb-btn-primary inline-flex items-center gap-2"
            >
              <Download size={16}/> Download the full PDF (1.24 MB)
            </a>
            <Link to="/register" className="nb-btn-secondary inline-flex items-center gap-2">
              Register now <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
            <div>Premium PDF</div>
            <div>·</div>
            <div>USDT · BEP20 · Live payouts</div>
            <div>·</div>
            <div>Updated 2026</div>
          </div>
        </div>
      </section>

      {/* Pages grid — mobile: single column, desktop: 2 cols */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Plan Details · Scroll through the pages</div>
        <div className="grid md:grid-cols-2 gap-6" data-testid="plan-pages-grid">
          {PAGES.map((src, idx) => (
            <button
              key={src}
              onClick={() => setLightbox(idx)}
              data-testid={`plan-page-${idx + 1}`}
              className="group text-left rounded-2xl overflow-hidden bg-[#0F1626] border border-white/5 hover:border-nb-cyan/40 transition"
            >
              <img
                src={src}
                alt={`Page ${idx + 1} — ${PAGE_TITLES[idx]}`}
                loading={idx < 2 ? "eager" : "lazy"}
                className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-300"
              />
              <div className="p-4 flex items-center justify-between text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-nb-cyan">Page {String(idx + 1).padStart(2, "0")}</div>
                  <div className="text-white font-heading">{PAGE_TITLES[idx]}</div>
                </div>
                <div className="text-slate-500 group-hover:text-nb-cyan transition text-xs flex items-center gap-1">
                  Zoom <ChevronRight size={14}/>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-2xl border border-nb-cyan/30 bg-nb-cyan/[0.04] p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-nb-cyan">Ready?</div>
            <h2 className="font-heading text-white text-2xl md:text-3xl mt-2">Start with as little as $10. Compound with your team.</h2>
            <p className="text-slate-400 mt-2 text-sm max-w-lg">Register in 60 seconds. Complete Live-Face KYC. Deposit USDT and activate your first bot the same day.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <a href={PDF_URL} download="next-billion-plan.pdf" data-testid="plan-cta-download" className="nb-btn-secondary inline-flex items-center gap-2">
              <Download size={14}/> PDF
            </a>
            <Link to="/register" data-testid="plan-cta-register" className="nb-btn-primary inline-flex items-center gap-2">
              Register <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-8 text-xs text-slate-500 flex flex-col md:flex-row justify-between items-center gap-3">
          <div>© 2026 Next Billion · Global Network · Building the future of decentralised trading</div>
          <a href="mailto:support@nextbillion.world" className="text-nb-cyan hover:text-white">support@nextbillion.world</a>
        </div>
      </footer>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="plan-lightbox"
        >
          <button
            onClick={(e)=>{ e.stopPropagation(); setLightbox(null); }}
            data-testid="plan-lightbox-close"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          >
            <X size={18}/>
          </button>
          <button
            onClick={(e)=>{ e.stopPropagation(); setLightbox((lightbox - 1 + PAGES.length) % PAGES.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            data-testid="plan-lightbox-prev"
          >
            <ChevronLeft size={22}/>
          </button>
          <button
            onClick={(e)=>{ e.stopPropagation(); setLightbox((lightbox + 1) % PAGES.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            data-testid="plan-lightbox-next"
          >
            <ChevronRight size={22}/>
          </button>
          <div className="max-h-[92vh] max-w-[92vw] flex flex-col items-center gap-3" onClick={(e)=>e.stopPropagation()}>
            <img src={PAGES[lightbox]} alt={`Page ${lightbox+1}`} className="max-h-[85vh] w-auto rounded-lg shadow-2xl"/>
            <div className="text-xs text-slate-400">
              Page {lightbox + 1} of {PAGES.length} · {PAGE_TITLES[lightbox]}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
